import { useState } from "react";
import Cookies from "js-cookie";

import {
  API_DOWNLOAD_TIMESHEET_EXCEL,
  API_DOWNLOAD_TIMESHEET_REPORT,
} from "../../../../../../../config/Endpoints.js";
import { getApi } from "../../../../../../utils/api.ts";
import { showError } from "../../../../../../utils/toast.ts";

interface UseDownloadAllocationTimesheetsReturn {
  downloadPdf: (from: string, to: string, filters: DownloadTimesheetFilters) => Promise<void>;
  downloadExcel: (from: string, to: string, filters: DownloadTimesheetFilters) => Promise<void>;
  isDownloading: boolean;
  error: string | null;
}

export interface DownloadTimesheetFilters {
  projectId: string;
  showDescription: boolean;
}

function getLoggedInEmployeeId(): string {
  const userData = localStorage.getItem("userData");
  if (!userData) return "";
  try {
    return String(JSON.parse(userData).id ?? "");
  } catch {
    return "";
  }
}

function triggerBlobDownload(data: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(data);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

export function useDownloadAllocationTimesheets(
  employeeId?: string,
): UseDownloadAllocationTimesheetsReturn {
  const resolvedEmployeeId = employeeId || getLoggedInEmployeeId();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async (
    url: string,
    filename: string,
    fallbackMessage: string,
  ): Promise<void> => {
    setIsDownloading(true);
    setError(null);

    try {
      const accessToken = Cookies.get("accessToken") ?? "";
      const { data } = await getApi<Blob>(url, accessToken, "blob");
      triggerBlobDownload(data, filename);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : fallbackMessage;
      setError(message);
      showError(message);
    } finally {
      setIsDownloading(false);
    }
  };

  const buildDownloadUrl = (
    endpoint: string,
    from: string,
    to: string,
    filters: DownloadTimesheetFilters,
  ) => `${endpoint}?${new URLSearchParams({
    employeeId: resolvedEmployeeId,
    from,
    to,
    projectId: filters.projectId,
    showDesc: String(filters.showDescription),
  }).toString()}`;

  const downloadPdf = async (from: string, to: string, filters: DownloadTimesheetFilters): Promise<void> => {
    const url = buildDownloadUrl(API_DOWNLOAD_TIMESHEET_REPORT, from, to, filters);
    await download(url, `timesheets_${from}_${to}.pdf`, "Failed to download PDF.");
  };

  const downloadExcel = async (from: string, to: string, filters: DownloadTimesheetFilters): Promise<void> => {
    const url = buildDownloadUrl(API_DOWNLOAD_TIMESHEET_EXCEL, from, to, filters);
    await download(url, `timesheets_${from}_${to}.xlsx`, "Failed to download Excel.");
  };

  return { downloadPdf, downloadExcel, isDownloading, error };
}
